import { flattenCellValueForQuickFilter } from 'shared/lib/quickFilterUtils';

/** 메인 그리드 퀵필터: row를 한 줄 검색 문자열로 평탄화 (Shipment Monitoring `__quick_search`와 동일 역할). */
function buildShipmentMainQuickSearch(row) {
    return flattenCellValueForQuickFilter(row).trim();
}

const CASE_CLOSURE_MAP = {
    inprogress: 'In Progress',
    complete: 'Complete',
    dispute: 'Disputing',
};

/**
 * Assemble the DataGrid rows for the shipment main table.
 *
 * Extracted from ShipmentMainTable's useMemo so the assembly is testable on its
 * own — the grid rebuilds this list in full on every cell update emit, and the
 * one guarantee callers rely on is that values inside a cell keep the order of
 * the source lists. Grouping walks each source list in its original order, so a
 * shipment's houses/containers/statuses appear exactly as the store holds them
 * even when several shipments' children are interleaved.
 */
export function buildShipmentRows({
    shipmentList,
    hblShipmentList,
    cntrShipmentList,
    splitShipmentList,
    statusList,
    mainConfig,
    tabValue,
    shipmentType,
}) {
    // Group the child lists by parent once. Rescanning the child lists per
    // shipment per column made one rebuild grow with shipments x children x columns.
    const groupBy = (list, keyOf) => {
        const grouped = new Map();
        (list || []).forEach((item) => {
            const key = keyOf(item);
            if (key === null || key === undefined) return;
            const bucket = grouped.get(key);
            if (bucket) bucket.push(item);
            else grouped.set(key, [item]);
        });
        return grouped;
    };
    const EMPTY = [];

    const parentKey = mainConfig.keyName[0];
    const hblsByShipmentId = groupBy(hblShipmentList, (house) => house[parentKey]);

    const shipmentIdByHblId = new Map();
    hblsByShipmentId.forEach((houses, shipmentId) => {
        houses.forEach((house) => shipmentIdByHblId.set(house.id, shipmentId));
    });
    const cntrKey = mainConfig.keyName[1];
    const cntrsByShipmentId = cntrKey
        ? groupBy(cntrShipmentList, (item) => shipmentIdByHblId.get(item[cntrKey]))
        : new Map();

    const statusesByParentId = groupBy(statusList, (status) => status.parent_id);

    /** OIM only: split(alias) rows hang off a shipment through a container. */
    let splitsByShipmentId = new Map();
    if (mainConfig.component === 'il_ocean_import') {
        const shipmentIdByCntrId = new Map();
        cntrsByShipmentId.forEach((cntrs, shipmentId) => {
            cntrs.forEach((cntr) => shipmentIdByCntrId.set(cntr.id, shipmentId));
        });
        splitsByShipmentId = groupBy(splitShipmentList, (alias) =>
            shipmentIdByCntrId.get(alias.il_ocean_import_cntr_id)
        );
    }

    const filteredShipment = shipmentList.filter((item) => item.case_closure === CASE_CLOSURE_MAP[shipmentType]);
    return filteredShipment.map((shipment) => {
        const matchedHouses = hblsByShipmentId.get(shipment.id) || EMPTY;
        const matchedHbls = mainConfig.hblOutputKeys.reduce((acc, key) => {
            if (key) {
                acc[key] = matchedHouses.map((house) => house[key]);
            }
            return acc;
        }, {});
        const dynamicMatchedValues = {};
        if (tabValue !== 4) {
            const matchedCntrs = cntrsByShipmentId.get(shipment.id) || EMPTY;
            for (let i = 1; i < mainConfig.outputKeys.length; i++) {
                const key = mainConfig.outputKeys[i];
                if (key) dynamicMatchedValues[key] = matchedCntrs.map((item) => item[key]);
            }
        } else {
            mainConfig.outputKeys.forEach((key) => {
                if (key) {
                    dynamicMatchedValues[key] = matchedHouses.map((alias) => alias[key]);
                }
            });
        }
        const matchedStatuses = (statusesByParentId.get(shipment.id) || EMPTY)
            .filter((status) => status.hidden !== true)
            .map((status) => status.message);
        let baseRow = {
            ...shipment,
            ...matchedHbls,
            ...dynamicMatchedValues,
            status: matchedStatuses,
        };

        /** OIM only: enrich with split(alias) fields so they are included in `__quick_search`. */
        if (mainConfig.component === 'il_ocean_import') {
            const aliasRows = splitsByShipmentId.get(shipment.id) || EMPTY;
            baseRow = {
                ...baseRow,
                alias_name: aliasRows.map((a) => a.alias_name).filter((v) => v != null && String(v).trim() !== ''),
                alias_bol_number: aliasRows
                    .map((a) => a.alias_bol_number)
                    .filter((v) => v != null && String(v).trim() !== ''),
                /** Split(alias) Deliver To — included in `__quick_search` only (container row may omit these). */
                alias_il_location_id_deliver_to: aliasRows
                    .map((a) => a.il_location_id_deliver_to)
                    .filter((v) => v != null && (typeof v !== 'string' || String(v).trim() !== '')),
                /** Split(alias) trucker — read by the Trucker Name column and `__quick_search`. */
                alias_il_trade_partner_id: aliasRows.map((a) => a.il_trade_partner_id).filter((v) => v?.name),
            };
        }

        return {
            ...baseRow,
            __quick_search: buildShipmentMainQuickSearch(baseRow),
        };
    });
}
